import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react'
import { useApi } from './ApiProvider'
import { errorObjectToString } from '../ApiClient'
import { AppToaster } from '../components/toaster'
import { useSurvey } from './SurveyProvider'
import { useWindow } from './WindowProvider'
import {
  COLORS,
  DARKER_COLORS,
} from '../components/segmentationexplorer/Colors'

/**
 * Assigns colors to priorities based on their index in the array
 * @param {Array} priorities - Array of priority objects
 * @returns {Array} - Array of priorities with assigned colors
 */
const assignColorsToPriorities = priorities => {
  return priorities.map((priority, index) => ({
    ...priority,
    color: COLORS[index % COLORS.length],
    darker_color: DARKER_COLORS[index % DARKER_COLORS.length],
  }))
}

const SegmentationPrioritiesContext = createContext()

/**
 * SegmentationPrioritiesProvider component provides context for segmentation priorities.
 *
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - Child components.
 * @returns {JSX.Element} The context provider.
 */
export function SegmentationPrioritiesProvider({ children }) {
  const [segmentations, setSegmentations] = useState()
  const [availableInputs, setAvailableInputs] = useState()
  const [needsRecalculationFlag, setNeedsRecalculationFlag] = useState(true)
  const [segmentationsScores, setSegmentationsScores] = useState([])
  const [isFetchingScores, setIsFetchingScores] = useState(false)
  const [lastSelectedSegmentations, setLastSelectedSegmentations] = useState([])

  // Constraints state (persist across component mounts)
  const [useRespondentsConstraint, setUseRespondentsConstraint] = useState(true)
  const [minRespondentsPerSegment, setMinRespondentsPerSegment] = useState(100)
  const [useDistributionConstraint, setUseDistributionConstraint] =
    useState(true)
  const [maxProperlyDistributedThreshold, setMaxProperlyDistributedThreshold] =
    useState(2.5)

  const api = useApi()
  const {
    surveyId,
    segmentationPriorities,
    setSegmentationPriorities,
    surveyDialogOpen,
    refreshSurveyData,
  } = useSurvey()
  const { fetchReports } = useWindow()

  const isOpen = surveyDialogOpen === 'SegmentationExplorerDialog'

  /**
   * Fetch segmentations from the API and update state.
   *
   * @returns {Promise<void>}
   */
  const fetchSegmentations = useCallback(async () => {
    if (!isOpen || api === undefined || surveyId === undefined) return
    const response = await api.get(`/survey/${surveyId}/segmentations`)
    if (response.ok) {
      setSegmentations(response.body)
    } else {
      const message = errorObjectToString(response.body.messages.json)
      AppToaster.show({ message, intent: 'danger', icon: 'error' })
    }
  }, [api, surveyId, isOpen])

  /**
   * Fetch segmentation priorities from the API and update state.
   *
   * @returns {Promise<void>}
   */
  const fetchSegmentationPriorities = async () => {
    if (!isOpen || api === undefined || surveyId === undefined) return
    const response = await api.get(
      `/survey_segmentation_priorities/${surveyId}`
    )
    if (response.ok) {
      // Assign colors to priorities based on their index
      const prioritiesWithColors = assignColorsToPriorities(response.body)
      setSegmentationPriorities(prioritiesWithColors)
    } else {
      const message = errorObjectToString(response.body.messages.json)
      AppToaster.show({ message, intent: 'danger', icon: 'error' })
    }
  }

  /**
   * Create a new segmentation priority.
   *
   * @param {string} priorityName - The name of the priority.
   * @param {number} weight - The weight for the priority.
   * @param {Array} selectedInputs - The inputs selected for the priority.
   * @returns {Promise<void>}
   */
  const createSegmentationPriority = async (
    priorityName,
    weight,
    selectedInputs
  ) => {
    if (!priorityName || selectedInputs.length === 0) {
      AppToaster.show({
        message: 'Name, weight, and at least one input are required.',
        intent: 'warning',
      })
      return
    }

    const inputIds = selectedInputs.map(input => input.id)
    const data = {
      name: priorityName,
      weight: weight || 0,
      input_ids: inputIds,
    }

    const response = await api.post('/segmentation_priority', data)
    if (response.ok) {
      AppToaster.show({
        message: `Segmentation priority created successfully.`,
        intent: 'success',
      })

      // Assign a color to the new priority
      const newPriority = response.body
      const existingPrioritiesLength = segmentationPriorities.length
      newPriority.darker_color =
        DARKER_COLORS[existingPrioritiesLength % DARKER_COLORS.length]
      newPriority.color = COLORS[existingPrioritiesLength % COLORS.length]

      // Update the priorities list
      setSegmentationPriorities(prev => [...prev, newPriority])
      setNeedsRecalculationFlag(true)

      // Refresh available inputs to reflect the new priorityId assignments and colors
      await fetchAllAvailableInputs()
    } else {
      AppToaster.show({
        message: `Failed to create segmentation priority. Server responded with status ${response.status}.`,
        intent: 'danger',
      })
    }
  }

  /**
   * Fetch all available inputs for the survey.
   * Optimized version that only fetches essential fields for segmentation priorities.
   *
   * @returns {Promise<void>}
   */
  const fetchAllAvailableInputs = async () => {
    if (!isOpen || api === undefined || surveyId === undefined) return
    const response = await api.get(
      `/survey/${surveyId}/segmentation_priority_inputs`
    )
    if (response.ok) {
      const inputs = response.body.map(input => ({
        id: input.id,
        name: input.name,
        label: input.label,
        priorityId: input.segmentation_priority_id,
      }))

      const inputsMap = new Map()
      inputs.forEach(input => {
        inputsMap.set(input.id, input)
      })
      setAvailableInputs(Array.from(inputsMap.values()))
    } else {
      const message = response.body?.messages?.json
        ? errorObjectToString(response.body.messages.json)
        : `Failed to fetch inputs: ${response.status} ${response.statusText}`
      AppToaster.show({ message, intent: 'danger', icon: 'error' })
    }
  }

  /**
   * Update a segmentation priority with new data.
   *
   * @param {number|string} id - The identifier of the priority.
   * @param {Object} newData - The new data for the priority.
   * @param {boolean} showSuccessMessage - Whether to show success toast (default: true).
   * @returns {Promise<void>}
   */
  const updateSegmentationPriority = useCallback(
    async (id, newData, showSuccessMessage = true) => {
      const response = await api.put(`/segmentation_priority/${id}`, newData)
      if (response.ok) {
        if (showSuccessMessage) {
          AppToaster.show({
            message: `Segmentation priority updated successfully.`,
            intent: 'success',
          })
        }

        // Update the priorities list
        setSegmentationPriorities(prev =>
          prev.map(p => (p.id === id ? { ...p, ...newData } : p))
        )
        setNeedsRecalculationFlag(true)
        return true
      } else {
        const message = errorObjectToString(response.body.messages.json)
        AppToaster.show({
          message: message,
          intent: 'danger',
        })
        return false
      }
    },
    [api, setSegmentationPriorities]
  )

  /**
   * Add inputs to an existing segmentation priority.
   *
   * @param {Array} inputs - The inputs to add.
   * @param {number|string} priority_id - The identifier of the priority.
   * @returns {Promise<void>}
   */
  const addInputsToPriority = useCallback(
    async (inputs, priority_id) => {
      if (inputs.length === 0) {
        AppToaster.show({
          message: 'At least one input is required.',
          intent: 'warning',
        })
        return
      }

      // Go fetch the inputs of this priority
      const priority = segmentationPriorities.find(p => p.id === priority_id)
      const old_input_ids = availableInputs
        .filter(input => input.priorityId === priority_id)
        .map(input => input.id)
      const new_input_ids = inputs.map(input => input.id)

      const success = await updateSegmentationPriority(
        priority_id,
        {
          input_ids: [...old_input_ids, ...new_input_ids],
        },
        false
      ) // Don't show success message yet

      if (success) {
        // Refresh available inputs to reflect the new priorityId assignments
        await fetchAllAvailableInputs()

        // Show success message after everything is complete
        AppToaster.show({
          message: `Inputs added to segmentation priority successfully.`,
          intent: 'success',
        })
      }
    },
    [
      segmentationPriorities,
      availableInputs,
      updateSegmentationPriority,
      fetchAllAvailableInputs,
    ]
  )

  /**
   * Remove inputs from an existing segmentation priority.
   *
   * @param {Array} inputs - The inputs to remove.
   * @param {number|string} priority_id - The identifier of the priority.
   * @returns {Promise<void>}
   */
  const removeInputsFromPriority = useCallback(
    async (inputs, priority_id) => {
      if (inputs.length === 0 || !inputs.some(input => input.priorityId)) {
        AppToaster.show({
          message: 'At least one input with priority is required .',
          intent: 'warning',
        })
        return
      }

      if (!inputs.every(input => input.priorityId === priority_id)) {
        AppToaster.show({
          message: 'You should select inputs with the same priority.',
          intent: 'warning',
        })
        return
      }

      // Go fetch the inputs of this priority
      const priority = segmentationPriorities.find(p => p.id === priority_id)
      const old_input_ids = availableInputs
        .filter(input => input.priorityId === priority_id)
        .map(input => input.id)
      const new_input_ids = inputs.map(input => input.id)

      const success = await updateSegmentationPriority(
        priority_id,
        {
          input_ids: old_input_ids.filter(id => !new_input_ids.includes(id)),
        },
        false
      ) // Don't show success message yet

      if (success) {
        // Refresh available inputs to reflect the new priorityId assignments
        await fetchAllAvailableInputs()

        // Show success message after everything is complete
        AppToaster.show({
          message: `Inputs removed from segmentation priority successfully.`,
          intent: 'success',
        })
      }
    },
    [
      segmentationPriorities,
      availableInputs,
      updateSegmentationPriority,
      fetchAllAvailableInputs,
    ]
  )

  /**
   * Delete a segmentation priority.
   *
   * @param {number|string} id - The identifier of the priority to delete.
   * @returns {Promise<void>}
   */
  const deleteSegmentationPriority = async id => {
    const response = await api.delete(`/segmentation_priority/${id}`)
    if (response.ok) {
      AppToaster.show({
        message: `Segmentation priority deleted successfully.`,
        intent: 'success',
      })

      // Get the current priorities and remove the deleted one
      const currentPriorities = segmentationPriorities
      const deletedIndex = currentPriorities.findIndex(p => p.id === id)
      const updatedPriorities = currentPriorities.filter(p => p.id !== id)

      // If the deleted priority was not the last one, update the colors
      if (deletedIndex !== currentPriorities.length - 1) {
        updatedPriorities.forEach((priority, index) => {
          const newColor = COLORS[index % COLORS.length]
          const newDarkerColor = DARKER_COLORS[index % DARKER_COLORS.length]
          // Update the color if it has changed
          if (
            priority.color !== newColor ||
            priority.darker_color !== newDarkerColor
          ) {
            priority.color = newColor
            priority.darker_color = newDarkerColor
          }
        })
      }
      setSegmentationPriorities(updatedPriorities)
      setNeedsRecalculationFlag(true)

      // Refresh available inputs to reflect priority removal and color updates
      await fetchAllAvailableInputs()
    } else {
      AppToaster.show({
        message: `Failed to delete segmentation priority. Server responded with status ${response.status}.`,
        intent: 'danger',
      })
    }
  }

  /**
   * Show a danger toast with the API error message.
   */
  const _showApiError = response => {
    const message =
      response.body?.description ||
      errorObjectToString(response.body?.messages?.json || {})
    AppToaster.show({ message, intent: 'danger', icon: 'error' })
  }

  /**
   * Common post-delete refresh: toast, re-fetch lists, invalidate scores.
   */
  const _refreshAfterDeletion = async (deletedIds, message) => {
    AppToaster.show({ message, intent: 'success', icon: 'tick' })
    await fetchSegmentations()
    setSegmentationsScores(prev =>
      prev.filter(s => !deletedIds.includes(s.id))
    )
    setNeedsRecalculationFlag(true)
    await Promise.all([
      fetchAllAvailableInputs(),
      refreshSurveyData(),
      fetchReports(),
    ])
  }

  /**
   * Analyze the impact of deleting a segmentation.
   */
  const analyzeSegmentationDeletion = async (segmentationId, options = {}) => {
    const response = await api.get(
      `/survey/${surveyId}/segmentation/${segmentationId}/analyze_deletion`,
      undefined,
      options
    )
    if (response.status === 0 || response.body === undefined) return null
    if (response.ok) return response.body
    _showApiError(response)
    return null
  }

  /**
   * Delete a segmentation and refresh the list.
   */
  const deleteSegmentation = async segmentationId => {
    const response = await api.delete(
      `/survey/${surveyId}/segmentation/${segmentationId}`
    )
    if (response.ok) {
      await _refreshAfterDeletion(
        [segmentationId],
        'Segmentation deleted successfully.'
      )
      return response.body
    }
    _showApiError(response)
    return null
  }

  /**
   * Analyze the impact of deleting multiple segmentations.
   */
  const analyzeBatchSegmentationDeletion = async (segmentationIds, options = {}) => {
    const response = await api.post(
      `/survey/${surveyId}/segmentations/analyze_batch_deletion`,
      { segmentation_ids: segmentationIds },
      options
    )
    if (response.status === 0 || response.body === undefined) return null
    if (response.ok) return response.body
    _showApiError(response)
    return null
  }

  /**
   * Delete multiple segmentations in a single batch operation.
   */
  const deleteSegmentations = async segmentationIds => {
    const response = await api.post(
      `/survey/${surveyId}/segmentations/delete`,
      { segmentation_ids: segmentationIds }
    )
    if (response.ok) {
      await _refreshAfterDeletion(
        segmentationIds,
        `${segmentationIds.length} segmentation(s) deleted successfully.`
      )
      return response.body
    }
    _showApiError(response)
    return null
  }

  /**
   * Fetch segmentation scores from the API and update state.
   *
   * @param {Array} selectedSegmentations - The segmentations to calculate scores for.
   * @returns {Promise<void>}
   */
  const fetchSegmentationsScores = useCallback(
    async (selectedSegmentations, options = undefined) => {
      // Validate that we have selected segmentations
      if (!selectedSegmentations || selectedSegmentations.length === 0) {
        AppToaster.show({
          message: 'Please select at least one segmentation to calculate scores.',
          intent: 'warning',
          icon: 'warning-sign',
        })
        return
      }

      setIsFetchingScores(true)

      // Store only essential information (id and name) of selected segmentations for future reference
      const essentialSegmentationsInfo = selectedSegmentations.map(seg => ({
        id: seg.id,
        name: seg.variable?.name,
      }))
      setLastSelectedSegmentations(essentialSegmentationsInfo)

      // Prepare request body with selected segmentations
      const requestBody = {
        selected_segmentations: selectedSegmentations.map(seg => seg.id),
      }

      const {
        useRespondentsConstraint,
        minRespondentsPerSegment,
        useDistributionConstraint,
        maxProperlyDistributedThreshold,
      } = options || {}

      if (useRespondentsConstraint) {
        requestBody.min_respondents_per_segment = minRespondentsPerSegment
      }
      if (useDistributionConstraint) {
        requestBody.max_properly_distributed_threshold =
          maxProperlyDistributedThreshold
      }

      const response = await api.post(
        `/survey/${surveyId}/segmentations_scores_table`,
        requestBody
      )

      if (response.ok) {
        const scoreData = response.body.score_data

        setSegmentationsScores(scoreData)
        // Reset the flag to indicate the scores are now up-to-date.
        setNeedsRecalculationFlag(false)
      } else {
        console.error(
          'API response error:',
          response.problem || 'Unknown error'
        )
        // show app toaster with error message
        const message = errorObjectToString(response.body.messages.json)
        AppToaster.show({
          message: `Error fetching ${message}`,
          intent: 'danger',
          icon: 'error',
        })
      }
      setIsFetchingScores(false)
    },
    [api, surveyId, setSegmentationsScores]
  )

  useEffect(() => {
    if (surveyId === undefined) return
    fetchSegmentations()
    fetchSegmentationPriorities()
    fetchAllAvailableInputs()
  }, [api, surveyId, isOpen])

  return (
    <SegmentationPrioritiesContext.Provider
      value={{
        segmentations,
        segmentationPriorities,
        availableInputs,
        needsRecalculationFlag,
        setNeedsRecalculationFlag,
        segmentationsScores,
        setSegmentationsScores,
        lastSelectedSegmentations,
        setLastSelectedSegmentations,
        createSegmentationPriority,
        updateSegmentationPriority,
        deleteSegmentationPriority,
        analyzeSegmentationDeletion,
        deleteSegmentation,
        analyzeBatchSegmentationDeletion,
        deleteSegmentations,
        addInputsToPriority,
        removeInputsFromPriority,
        fetchSegmentationsScores,
        isFetchingScores,
        useRespondentsConstraint,
        setUseRespondentsConstraint,
        minRespondentsPerSegment,
        setMinRespondentsPerSegment,
        useDistributionConstraint,
        setUseDistributionConstraint,
        maxProperlyDistributedThreshold,
        setMaxProperlyDistributedThreshold,
      }}
    >
      {children}
    </SegmentationPrioritiesContext.Provider>
  )
}

export const useSegmentationPriorities = () => {
  const context = useContext(SegmentationPrioritiesContext)
  if (!context) {
    throw new Error(
      'useSegmentationPriorities must be used within a SegmentationPrioritiesProvider'
    )
  }
  return context
}
